import { ArrowRight } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import type { Template, TemplateSummary } from "../../api/types";
import { pageCn } from "./host/hostLabels";
import { NO_NEWS_LIST_HINT } from "./TemplateCapabilities";

const COUNT_ROWS = [
  { key: "heading", label: "标题" },
  { key: "para", label: "段落" },
  { key: "slot", label: "卡片文字" },
  { key: "image", label: "图片" },
] as const;

/**
 * 屏 2 右栏(宿主模板,A.46 / A.57):不画 schema 分组 —— 宿主模板的采集清单与模板无关(统一用 corporate-v1 的事实清单,
 * 采集页里看),这里只说这套模板**长什么样**:页列表 + 四类可改节点计数(`profileSummary`)+ 是否含产品详情页。
 * ★ 详情还没回来时用卡片摘要先画页列表,计数位画骨架;详情回了但没带 `profileSummary`(老后端)就只列页、写「节点计数暂缺」。
 */
export function HostProfilePanel({
  summary,
  template,
  error,
  onRetry,
  onStart,
}: {
  summary: TemplateSummary;
  /** null = 还在拉。 */
  template: Template | null;
  error: string | null;
  onRetry: () => void;
  onStart: () => void;
}) {
  const profile = template?.profileSummary;
  const pages = profile?.pages ?? summary.pages;
  return (
    <aside
      aria-labelledby="website-host-profile-title"
      className={cn("flex flex-col gap-4 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="website-host-profile-title" className="text-sm font-semibold text-foreground">
          {summary.name} · 模板结构
        </h2>
        <Badge variant="secondary" className="normal-case tracking-normal">
          {pages.length} 页
        </Badge>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        资料按统一的事实清单采集(企业名、电话、地址、产品…),出稿时 AI
        按这套模板的版式,把你的资料写进对应位置;
        模板里原企业的名字、图片和外链发布前都会换掉或清空。
      </p>

      {error ? (
        <QueryErrorBanner isError message={error} onRetry={onRetry} />
      ) : (
        <>
          <section aria-label="页面" className="space-y-1.5">
            <h3 className="text-xs font-semibold text-foreground">页面</h3>
            <ul className="flex flex-wrap gap-1.5 text-2xs">
              {pages.map((p) => (
                <li
                  key={p}
                  className="rounded border border-border px-1.5 py-0.5 text-muted-foreground"
                >
                  {pageCn(p)}
                </li>
              ))}
            </ul>
          </section>
          <section aria-label="可改内容" className="space-y-1.5">
            <h3 className="text-xs font-semibold text-foreground">可改内容</h3>
            {!template ? (
              <Skeleton className="h-10 w-full" />
            ) : profile ? (
              <dl className="grid grid-cols-4 gap-2 text-center">
                {COUNT_ROWS.map(({ key, label }) => (
                  <div
                    key={key}
                    className="flex flex-col-reverse rounded-md border border-border/70 bg-muted/30 px-2 py-1.5"
                  >
                    <dt className="text-2xs text-muted-foreground">{label}</dt>
                    <dd className="text-base font-semibold tabular-nums text-foreground">
                      {profile.nodeCounts[key]}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-xs text-muted-foreground">节点计数暂缺。</p>
            )}
          </section>
          {summary.dynamic && (
            <p
              role="note"
              className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground"
            >
              含产品详情页:发布前会先把你的产品同步到宿主,产品列表与详情页由模板渲染。
            </p>
          )}
          {summary.articleList === true && (
            <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
              新闻自动更新:新闻舱发到官网的文章会自动出现在新闻列表里,也可以建「官网栏目」定期发。
            </p>
          )}
          {summary.articleList === false && (
            <p className="rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              {NO_NEWS_LIST_HINT}
            </p>
          )}
        </>
      )}

      <Button className="w-full" onClick={onStart}>
        用这套建站 · 一键生成
        <ArrowRight />
      </Button>
    </aside>
  );
}
